import axios from "axios";
import { attributionHeaders } from "./ambassadorRef";
import { isNative, getToken, setToken } from "./nativeSession";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const extra = (config.url || "").startsWith("/auth/") ? { ...attributionHeaders() } : {};
  if (isNative()) {
    extra["X-Metier-Native"] = "1";
    const tok = getToken();
    if (tok) extra.Authorization = `Bearer ${tok}`;
  }
  config.headers = { ...(config.headers || {}), ...extra };
  return config;
});

// One-shot boot diagnostic — printed to Safari Web Inspector / Xcode
// console when the native TestFlight app launches. Confirms the packaged
// bundle knows which API to hit and which build it is.
if (typeof window !== "undefined" && !window.__metier_boot_logged) {
  window.__metier_boot_logged = true;
  // eslint-disable-next-line no-console
  console.log("[Métier] boot", {
    api_base_url: BASE,
    native_build: process.env.REACT_APP_NATIVE_BUILD || "(dev)",
    ua: navigator.userAgent,
  });
}

// Response interceptor: log every /spaces fetch so we can prove the
// native app reaches the production API and receives the same records.
api.interceptors.response.use(
  (r) => {
    if (isNative() && r.data && typeof r.data === "object" && r.data.session_token) {
      setToken(r.data.session_token);
    }
    try {
      const url = (r.config && r.config.url) || "";
      if (url.startsWith("/spaces") || url.startsWith("/hours") || url === "/auth/me") {
        const n = Array.isArray(r.data) ? r.data.length : undefined;
        // eslint-disable-next-line no-console
        console.log(`[Métier] GET ${BASE}${url} → ${r.status}${n !== undefined ? " · " + n + " records" : ""}`);
      }
    } catch { /* noop */ }
    return r;
  },
  (err) => {
    // Normalize FastAPI/Pydantic 422 error payloads. `detail` can be a list of
    // {msg, loc, type, ...} objects — if a component passes that array to a
    // toast/renderer, React throws "Objects are not valid as a React child"
    // and unmounts the entire tree → blank screen. Convert to a readable
    // string here so every downstream `err.response.data.detail` is safe.
    try {
      const d = err && err.response && err.response.data;
      if (d && Array.isArray(d.detail)) {
        const msg = d.detail
          .map((x) => {
            if (typeof x === "string") return x;
            if (x && typeof x === "object") {
              const loc = Array.isArray(x.loc) ? x.loc.filter((s) => s !== "body").join(".") : "";
              return loc ? `${loc}: ${x.msg || x.type || "invalid"}` : (x.msg || x.type || JSON.stringify(x));
            }
            return String(x);
          })
          .filter(Boolean)
          .join(" · ");
        err.response.data.detail = msg || "Request failed";
      } else if (d && d.detail && typeof d.detail === "object") {
        // Session 59.0 · Preserve structured errors that carry an
        // `error_code` (e.g. invalid_zip). These are intentional and the
        // UI branches on the code — flattening to `msg` would break it.
        if (!d.detail.error_code) {
          err.response.data.detail = d.detail.msg || JSON.stringify(d.detail);
        }
      }
    } catch { /* noop — keep original error */ }
    return Promise.reject(err);
  },
);

export default api;
